import axios from 'axios'
import { apiClient } from '../client'
import type { ItemsResponse } from '../../app/modules/types/chatBot'

/**
 * A patch session as the service SERVES it.
 *
 * Note the asymmetry with `StartPatchSessionParams` below: responses keep the
 * `target_*` prefix, requests dropped it. The reader is a different shape from
 * the writer, and getting them backwards produces a 400 that complains about
 * `host` while your code is talking about `target_host`.
 */
export interface PatchSession {
  id: string
  chat_id: string
  /**
   * Trustworthy on expiry: a session past `expires_at` is settled to `expired`
   * the next time anything reads it, so drive the UI off this rather than
   * comparing `expires_at` to the clock. The transition is visible on the next
   * poll or reload, not at the exact second it lapses.
   */
  status: 'pending_authorization' | 'connected' | 'expired' | 'ended' | 'revoked'
  target_host: string
  target_port: number
  target_username: string
  path_scope?: string | null
  /** The public half only. The private half never leaves the server. */
  public_key: string
  expires_at: string
  connected_at: string | null
  ended_at: string | null
  created_at: string
}

/**
 * Rows from `GET /chat/patch-sessions` — same shape; `chat_id` is on the base.
 * The listing is caller-scoped: a user sees only their own sessions.
 */
export type ActivePatchSession = PatchSession

/** The START request body. `target_*` was renamed; `path_scope` was not. */
export interface StartPatchSessionParams {
  /** Max 255 characters. */
  host: string
  /** Optional; defaults to 22. */
  port?: number
  /** Max 64 characters — narrower than Django's 255. Validate client-side. */
  username: string
  /**
   * A HINT placed in the model's prompt. Nothing enforces it — `run_command`
   * can leave any directory in one step. Word the UI as "suggest a working
   * directory", never as a restriction. Human approval is the real boundary.
   *
   * Max 500 characters.
   */
  path_scope?: string
}

export interface ConnectivityResult {
  connected: boolean
  /** Present only when `connected` is false. Comes straight from the SSH client. */
  reason?: string
}

export const patchSessionApi = {
  /**
   * The chat's session, or null. The service returns 200 `{session: null}` when
   * there is none — a 404 now means the CHAT does not exist (or belongs to
   * another org), which we also surface as null so a chat that vanished does
   * not explode the panel.
   *
   * So `null` CONFLATES "this chat has no patch session" with "this chat is
   * gone". Do not read a missing-chat signal out of it: the chat query owns
   * that, and it 404s on the same condition. Here, null means "render the start
   * form", which is the right thing to do in both cases.
   */
  get: async (chatId: string): Promise<PatchSession | null> => {
    try {
      const { data } = await apiClient.get<{ session: PatchSession | null }>(
        `/chat/${chatId}/patch-session`,
      )
      return data.session
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null
      throw err
    }
  },

  /**
   * Start a session. 400 if the chat has no subject (free chats cannot patch).
   * A chat may now hold several live sessions; a second start succeeds. The
   * unique database index that produced the 409 is gone.
   */
  start: async (chatId: string, params: StartPatchSessionParams): Promise<PatchSession> => {
    const { data } = await apiClient.post<PatchSession>(
      `/chat/${chatId}/patch-session`,
      params,
    )
    return data
  },

  /**
   * Test SSH reachability. Returns a verdict, NOT a session — the old code had
   * to diff `status` to tell success from failure. A first-attempt `false` is
   * the normal case (the key is not installed yet), so render it as guidance
   * with a retry, never as a red error.
   *
   * A 404 here means there is no live session (the panel is stale). It is NOT
   * swallowed like `end`'s, because the caller has to react — refetch the
   * session and fall back to the start form. Note that it is also exempt from
   * the global error toast, so an unhandled rejection is complete silence: the
   * caller owns rendering this.
   */
  checkConnectivity: async (chatId: string): Promise<ConnectivityResult> => {
    const { data } = await apiClient.post<ConnectivityResult>(
      `/chat/${chatId}/patch-session/check-connectivity`,
    )
    return data
  },

  /**
   * End every live session in the chat. Ending only the most recent session
   * would leave a private key installed and usable on another machine while the
   * UI reported nothing active — the user would believe they revoked access to
   * a production box and have not.
   *
   * The endpoint returns 204 on success or 404 if there was nothing live — the
   * latter is swallowed, since the UI fires this defensively on close.
   */
  end: async (chatId: string): Promise<void> => {
    try {
      await apiClient.post(`/chat/${chatId}/patch-session/end`)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return
      throw err
    }
  },

  /**
   * Caller's live sessions, indexed for chat-list badges. The listing is
   * caller-scoped, so a user sees only their own sessions and the badge no
   * longer appears on a colleague's chat.
   */
  listActive: async (): Promise<ActivePatchSession[]> => {
    const { data } = await apiClient.get<ItemsResponse<ActivePatchSession>>('/chat/patch-sessions')
    return data.items
  },
}
